import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import Dialog from '@atoms/Dialog';
import useIntroMadeByMeById from '@hooks/useIntroMadeByMeById';
import PlayableVideoCircle from '@molecules/PlayableVideoCircle';
import TextInputCard from '@molecules/TextInputCard';
import IntroPersonSelection from '@organisms/IntroPersonSelection';
import useStartOrGetConversation from '@queries/conversations';
import {
  useAcceptIntroAsIntroducer,
  useCreateIntro,
  useDenyIntro,
} from '@queries/introductions';
import { useGetProfile } from '@sunrise-ui/api-client';
import { type Profile } from '@sunrise-ui/api/profile';
import { withAuthGate } from '@sunrise-ui/composites';
import {
  Button,
  Colors,
  TextBold,
  TextReg,
  defaultBack,
  useDynamicStyles,
  useSafeLocalSearchParams,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';

function CreateIntroScreen() {
  const styles = useDynamicStyles(createStyles);

  // userId starts a new intro, id is for an existing intro
  const { id, userId } = useSafeLocalSearchParams<{
    id?: string;
    userId?: string;
  }>();
  const { mutate: createIntro } = useCreateIntro();
  const { mutate: acceptIntro } = useAcceptIntroAsIntroducer();
  const { mutate: denyIntro } = useDenyIntro();

  const deleteIntro = async () => {
    if (id) {
      denyIntro(id, {
        onSuccess: defaultBack(),
      });
    }
  };

  const {
    isLoading: isLoadingInitialRequestor,
    data: initialRequestorProfile,
  } = useGetProfile(userId);
  const {
    isLoading: isLoadingIntro,
    requestor: requestorIntroHalf,
    acceptor: acceptorIntroHalf,
  } = useIntroMadeByMeById({ introId: id });
  const { isLoading: isLoadingRequestor, data: requestorProfile } =
    useGetProfile(requestorIntroHalf?.person.user_id);
  const { isLoading: isLoadingAcceptor, data: acceptorProfile } = useGetProfile(
    acceptorIntroHalf?.person.user_id,
  );
  const { mutate: startOrGetConversation } = useStartOrGetConversation();

  const [requestor, setRequestor] = useState<Profile | undefined>(
    initialRequestorProfile ?? requestorProfile,
  );
  const [acceptor, setAcceptor] = useState<Profile | undefined>(
    acceptorProfile,
  );
  const [dialogVisible, setDialogVisible] = useState(false);

  if (
    isLoadingInitialRequestor ||
    isLoadingIntro ||
    isLoadingRequestor ||
    isLoadingAcceptor
  )
    return null;

  const sentRequestorMessage =
    requestorIntroHalf?.introduced_to.introducer_message;
  const requestorVideo = requestorIntroHalf?.introduced_to.intro_video_url;
  const receivedVideo = Boolean(requestorVideo);
  const sentVideo = acceptorIntroHalf?.introduced_to.introducer_message;

  const showDialog = () => {
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
  };

  const sendVideoRequest = async (values: { message: string }) => {
    if (requestor && acceptor) {
      createIntro(
        {
          requestorId: requestor.user_id,
          acceptorId: acceptor.user_id,
          message: values.message,
        },
        {
          onSuccess: (response) => {
            // redirect to the official intro page now that it's been created
            router.replace(
              `/introduce?id=${response.data.introduction[0].introduced_to.id}`,
            );
          },
        },
      );
    }
  };

  const followUp = async (person: Profile) => {
    startOrGetConversation(
      {
        userId: person.user_id,
      },
      {
        onSuccess: (conversation) => {
          router.navigate(`/inbox/connected/${conversation._id}`);
        },
      },
    );
  };

  const sendVideo = async (values: { message: string }) => {
    if (requestor && acceptor) {
      acceptIntro({
        requestorId: requestor.user_id,
        acceptorId: acceptor.user_id,
        message: values.message,
      });
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.introSteps}>
        <IntroPersonSelection
          requestor={requestor}
          setRequestor={setRequestor}
          acceptor={acceptor}
          setAcceptor={setAcceptor}
          canSwitch={Boolean(acceptor) && !sentRequestorMessage}
        />
        {requestor && acceptor && !sentRequestorMessage && (
          <TextInputCard
            header={
              <>
                1. Request intro video from{' '}
                <TextBold>
                  {requestor.first_name} {requestor.last_name}
                </TextBold>
              </>
            }
            label={`Give ${requestor.first_name} some tips*`}
            buttonText="Send"
            buttonOnPress={sendVideoRequest}
          />
        )}
        {requestor && sentRequestorMessage && !receivedVideo && (
          <View style={styles.card}>
            <TextReg style={styles.cardHeader}>
              Awaiting intro video from{' '}
              <TextBold>
                {requestor.first_name} {requestor.last_name}
              </TextBold>
            </TextReg>
            <Button
              onPress={() => {
                void followUp(requestor);
              }}
            >
              Follow Up
            </Button>
          </View>
        )}
        {requestor && receivedVideo && (
          <View style={styles.card}>
            <TextReg style={styles.cardHeader}>
              Intro video from{' '}
              <TextBold>
                {requestor.first_name} {requestor.last_name}
              </TextBold>
            </TextReg>
            <PlayableVideoCircle
              videoUrl={requestorVideo}
              userId={requestor.user_id}
            />
          </View>
        )}
        {acceptor && !sentVideo && (
          <TextInputCard
            header={
              <>
                2. Send intro video to{' '}
                <TextBold>
                  {acceptor.first_name} {acceptor.last_name}
                </TextBold>
              </>
            }
            label={`Give ${acceptor.first_name} some context*`}
            buttonText="Send"
            buttonOnPress={sendVideo}
            disabled={!sentRequestorMessage || !receivedVideo}
          />
        )}
        {acceptor && sentVideo && (
          <View style={styles.card}>
            <TextReg style={styles.cardHeader}>
              Waiting for{' '}
              <TextBold>
                {acceptor.first_name} {acceptor.last_name}
              </TextBold>{' '}
              to accept
            </TextReg>
            <Button
              onPress={() => {
                void followUp(acceptor);
              }}
            >
              Follow Up
            </Button>
          </View>
        )}
      </View>
      {sentRequestorMessage && (
        <Button onPress={showDialog}>Cancel Intro</Button>
      )}
      <Dialog
        visible={dialogVisible}
        hide={hideDialog}
        title="Are you sure?"
        content="This will delete the intro, as if it never happened, and can't be undone."
        primaryText="Delete Intro"
        primaryOnPress={deleteIntro}
        secondaryText="Nevermind"
        secondaryOnPress={hideDialog}
      />
    </ScrollView>
  );
}

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: isMobile ? undefined : 1,
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: isMobile ? 12 : isTablet ? 16 : 20,
      padding: isMobile ? 12 : isTablet ? 16 : 20,
      width: '100%',
      // to push the cancel button down close to the bottom on mobile, there may be a better way
      minHeight: '90%',
    },
    introSteps: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: isMobile ? 12 : isTablet ? 16 : 20,
      width: '100%',
    },
    card: {
      width: isMobile ? '100%' : isTablet ? '60%' : '40%',
      backgroundColor: Colors.opacity05,
      borderRadius: 16,
      padding: 16,
      gap: 16,
      alignItems: 'center',
    },
    cardHeader: {
      width: '100%',
      textAlign: 'center',
    },
  });

export default withAuthGate(CreateIntroScreen);
